import React, { useEffect, useRef, useState } from "react";

export default function SearchSelectInput({
  name,
  label,
  required = false,
  options = [],
  placeholder = "Select...",
  value = "",
  onChange,
  error = "",
  className = "",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selectedOption = options.find((option) => option.value === value);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(search.toLowerCase()),
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
        setSearch(selectedOption?.label || "");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedOption]);

  const handleFocus = () => {
    if (disabled) return;
    setOpen(true);
    setSearch("");
  };

  const handleChange = (event) => {
    const searchValue = event.target.value;

    setSearch(searchValue);
    setOpen(true);
  };

  const handleSelect = (option) => {
    onChange({target: { name, value: option.value }});

    setSearch(option.label);
    setOpen(false);
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onChange({ target: {name,value: ""}});
    setSearch("");
    setOpen(true);

    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-gray-700">
          {label}

          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          name={name}
          value={open ? search : selectedOption?.label || ""}
          onFocus={handleFocus}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          className={`h-9 w-full rounded-lg border bg-white px-3 pr-10 text-sm outline-none transition
            ${
              error
                ? "border-red-500 focus:border-red-500"
                : open
                  ? "border-primary-500 ring-2 ring-primary-500/20"
                  : "border-gray-300"
            }
            ${
              disabled
                ? "cursor-not-allowed bg-gray-100 text-gray-500"
                : "cursor-pointer text-gray-700"
            }
          `}
        />

        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            if (open) {
              setOpen(false);
              setSearch(selectedOption?.label || "");
            } else {
              inputRef.current?.focus();
            }
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 px-1 text-gray-400"
        >
          <span
            className={`inline-block text-xs transition-transform ${
              open ? "rotate-180" : ""
            }`}
          >
            ▼
          </span>
        </button>
      </div>

      {open && !disabled && (
        <div className="absolute left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={() => handleSelect(option)}
                className={`block w-full px-3 py-2 text-left text-sm transition
                  ${
                    option.value === value
                      ? "bg-gray-100 font-medium text-gray-900"
                      : "text-gray-700 hover:bg-gray-50"
                  }
                `}
              >
                {option.label}
              </button>
            ))
          ) : (
            <div className="px-3 py-2.5 text-sm text-gray-500">
              No results found
            </div>
          )}
        </div>
      )}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
    </div>
  );
}
